import React, { Component } from 'react';
import ReactCrop from 'react-image-crop';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Button, Modal, SecondaryButton } from '../';

import css from './ImageCropModal.module.css';

/**
 * ImageCropModal
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {Function} props.onSave - Function to call when save button is clicked with cropped image
 * @param {File} props.imageFile - The image file to crop
 * @param {number} props.aspectRatio - Aspect ratio for cropping (default: 1 for square)
 * @param {boolean} props.circularCrop - Whether to use circular crop (default: false)
 * @param {string} props.title - Custom title for the modal (optional, defaults to translation)
 * @param {string} props.instructions - Custom instructions text (optional, defaults to translation)
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class ImageCropModalComponent extends Component {
  constructor(props) {
    super(props);

    const { aspectRatio = 1 } = props;

    this.state = {
      crop: {
        unit: '%',
        width: 90,
        aspect: aspectRatio,
      },
      croppedImageUrl: null,
      completedCrop: null,
    };

    this.imageRef = React.createRef();
    this.canvasRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.imageFile !== this.props.imageFile && this.props.imageFile) {
      this.loadImage();
    }
  }

  componentDidMount() {
    // Dynamically import CSS only in the browser to avoid Node parsing CSS
    if (typeof window !== 'undefined') {
      import('react-image-crop/dist/ReactCrop.css');
    }
    // Load image if it's already provided
    if (this.props.imageFile) {
      this.loadImage();
    }
  }

  loadImage = () => {
    const { imageFile } = this.props;
    if (imageFile) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.setState({
          croppedImageUrl: reader.result,
          completedCrop: null, // Reset completed crop when new image loads
        });
      });
      reader.readAsDataURL(imageFile);
    }
  };

  onCropChange = crop => {
    this.setState({ crop });
    // Also set as completed crop if it has valid dimensions
    if (crop && crop.width > 0 && crop.height > 0) {
      this.setState({ completedCrop: crop });
    }
  };

  onCropComplete = crop => {
    // Only set completedCrop if crop has valid dimensions
    if (crop && crop.width > 0 && crop.height > 0) {
      this.setState({ completedCrop: crop });
    }
  };

  getCroppedImg = (image, crop, fileName) => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            console.error('Canvas is empty');
            return;
          }
          blob.name = fileName;
          resolve(blob);
        },
        'image/jpeg',
        0.9
      );
    });
  };

  handleSave = async () => {
    const { onSave, imageFile } = this.props;
    const { completedCrop } = this.state;

    if (!completedCrop || !this.imageRef.current) {
      return;
    }

    try {
      const croppedImageBlob = await this.getCroppedImg(
        this.imageRef.current,
        completedCrop,
        imageFile.name
      );

      onSave(croppedImageBlob);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  render() {
    const { 
      isOpen, 
      onClose, 
      aspectRatio = 1, 
      circularCrop = false, 
      title,
      instructions,
      intl 
    } = this.props;
    const { crop, croppedImageUrl, completedCrop } = this.state;

    const saveButtonDisabled = !completedCrop;

    // Use custom title/instructions if provided, otherwise fall back to default translations
    const modalTitle = title || intl.formatMessage({ id: 'ImageCropModal.title' });
    const modalInstructions = instructions || intl.formatMessage({ id: 'ImageCropModal.instructions' });

    // Handle body scroll management. `position: fixed` on body without a
    // top offset snaps the page to its viewport-relative static position
    // (visually the top) the instant it's applied — and since that never
    // touches window.scrollY, restoring `position: ''` alone leaves the
    // browser trying to reconcile a scroll position the fixed body ignored
    // while locked, which is where the "can't scroll after closing" bug
    // came from. Saving/restoring the offset explicitly, the same way
    // Page.js already does for its own scroll lock, avoids that.
    const handleManageDisableScrolling = (componentId, disableScrolling) => {
      if (disableScrolling) {
        this.lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.lockedScrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      } else {
        const scrollY = this.lockedScrollY || 0;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      }
    };

    return (
      <Modal
        id="ImageCropModal"
        isOpen={isOpen}
        onClose={onClose}
        usePortal
        containerClassName={css.modal}
        contentClassName={css.modalContent}
        onManageDisableScrolling={handleManageDisableScrolling}
      >
        <div className={css.cropContainer}>
          {/* <h4 className={css.title}>
            {modalTitle}
          </h4> */}

          <div className={css.cropWrapper}>
            {croppedImageUrl && (
              <ReactCrop
                crop={crop}
                onChange={this.onCropChange}
                onComplete={this.onCropComplete}
                aspect={aspectRatio}
                circularCrop={circularCrop}
              >
                <img
                  ref={this.imageRef}
                  alt="Crop me"
                  src={croppedImageUrl}
                  className={css.cropImage}
                />
              </ReactCrop>
            )}
          </div>

          <div className={css.instructions}>
            {modalInstructions}
          </div>

          <div className={css.buttonContainer}>
            <Button
              onClick={this.handleSave}
              disabled={saveButtonDisabled}
              className={css.saveButton}
            >
              <FormattedMessage id="ImageCropModal.save" />
            </Button>
            <SecondaryButton onClick={onClose} className={css.cancelButton}>
              <FormattedMessage id="ImageCropModal.cancel" />
            </SecondaryButton>
          </div>
        </div>

        {/* Hidden canvas for image processing */}
        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
      </Modal>
    );
  }
}

const ImageCropModal = injectIntl(ImageCropModalComponent);

ImageCropModal.displayName = 'ImageCropModal';

export default ImageCropModal;
